// The generic input renderers. An action declares inputs by kind; this is the only place
// that knows how each kind looks, so a new action never brings a screen of its own.

import React, { useEffect, useMemo, useState } from 'react';
import { useApi } from '../../apiHelpers';
import { BTN_TONAL, FOCUS, MOTION } from '../../Requisitions/buttonStyles';
import { CheckIcon } from '../icons';
import { FIELD, KeyStrip, Notice } from '../ui';
import type { ChoiceInput, CustomerInput, MarkerInput } from './types';

export const INTERNAL_LABEL = 'Return to Internal Fleet';

interface Customer {
  id: number;
  name: string;
  is_customer: boolean;
}

interface InputProps<T> {
  input: T;
  value: unknown;
  /** `label` is the words the confirm step uses for this answer. */
  onChange: (value: unknown, label: string) => void;
}

const ROW = `flex min-h-[2.75rem] w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-base np-state ${FOCUS} ${MOTION}`;

const CustomerPicker: React.FC<InputProps<CustomerInput>> = ({ input, value, onChange }) => {
  const api = useApi();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');

  const load = async () => {
    setFailed(false);
    try {
      const rows = (await api.get('/customers')) as Customer[];
      setCustomers(rows.filter((r) => r.is_customer));
    } catch {
      setFailed(true);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? customers.filter((c) => c.name.toLowerCase().includes(q)) : customers;
  }, [customers, query]);

  const row = (key: string, selected: boolean, text: string, pick: () => void) => (
    <li key={key}>
      <button
        type="button"
        aria-pressed={selected}
        onClick={pick}
        className={`${ROW} ${selected ? 'bg-primary/15 text-primary' : 'bg-surface-container text-on-surface'}`}
      >
        <span className="min-w-0 flex-1 truncate">{text}</span>
        {selected && <CheckIcon className="h-5 w-5 shrink-0" />}
      </button>
    </li>
  );

  return (
    <div>
      <label htmlFor="marker-customer-search" className="mb-1 block text-sm text-on-surface-variant">{input.label}</label>
      <input
        id="marker-customer-search"
        type="search"
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        placeholder="Find a customer"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className={`${FIELD} mb-2`}
      />
      {failed && (
        <div className="mb-2 flex flex-col items-start gap-2">
          <Notice tone="miss">The customer list did not load. Return to Internal Fleet still works.</Notice>
          <button type="button" onClick={load} className={`${BTN_TONAL} min-h-[2.75rem] px-5`}>Try again</button>
        </div>
      )}
      <ul aria-label="Customers" className="flex max-h-[45vh] flex-col gap-1 overflow-y-auto">
        {row('internal', value === null, INTERNAL_LABEL, () => onChange(null, INTERNAL_LABEL))}
        {shown.map((c) => row(String(c.id), value === c.id, c.name, () => onChange(c.id, `customer ${c.name}`)))}
        {!failed && customers.length > 0 && shown.length === 0 && (
          <li className="px-3 py-2 text-sm text-on-surface-variant">Nothing matches "{query.trim()}".</li>
        )}
      </ul>
    </div>
  );
};

const ChoicePicker: React.FC<InputProps<ChoiceInput>> = ({ input, value, onChange }) => {
  const current = input.options.find((o) => o.value === value);
  return (
    <div>
      <p className="mb-1 text-sm text-on-surface-variant">{input.label}</p>
      <KeyStrip
        label={input.label}
        options={input.options.map((o) => ({ key: String(o.value), label: o.label }))}
        value={current ? String(current.value) : ''}
        onChange={(key) => {
          const picked = input.options.find((o) => String(o.value) === key);
          if (picked) onChange(picked.value, picked.label.toLowerCase());
        }}
      />
    </div>
  );
};

/** One input, by kind. */
export const MarkerInputField: React.FC<InputProps<MarkerInput>> = ({ input, value, onChange }) =>
  input.kind === 'customer'
    ? <CustomerPicker input={input} value={value} onChange={onChange} />
    : <ChoicePicker input={input} value={value} onChange={onChange} />;
